import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  HelpCircle,
  Truck,
  X,
  XCircle,
} from 'lucide-react';

import { customerFulfillmentService } from '@/services/customerFulfillment/customerFulfillment.service';
import type {
  ComponentEvaluation,
  FulfillmentReason,
  FulfillmentStatus,
  LineEvaluation,
  OrderDetail,
} from '@/services/customerFulfillment/customerFulfillment.types';

interface Props {
  salesOrderId: number | null;
  warehouseId?: number | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (Number.isInteger(value)) return value.toLocaleString('es-ES');
  return Number(value).toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function deriveLineStatus(line: LineEvaluation): FulfillmentStatus {
  if (line.status) return line.status;
  if (!line.determinable) return 'NOT_DETERMINABLE';
  if (line.at_risk) return 'AT_RISK';
  if (line.needs_purchase) return 'NEEDS_PURCHASE_ON_TIME';
  return line.complies ? 'OK' : 'AT_RISK';
}

function deriveComponentStatus(comp: ComponentEvaluation): FulfillmentStatus {
  if (comp.status) return comp.status;
  return comp.complies ? 'OK' : 'AT_RISK';
}

function StatusPill({ status }: { status: FulfillmentStatus }) {
  const { t } = useTranslation();
  const base =
    'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap';
  switch (status) {
    case 'OK':
      return (
        <span className={`${base} bg-green-50 text-green-700 border-green-200`}>
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t('customerFulfillment.status.fulfilled')}
        </span>
      );
    case 'NEEDS_PURCHASE_ON_TIME':
      return (
        <span className={`${base} bg-amber-50 text-amber-800 border-amber-200`}>
          <Clock3 className="w-3.5 h-3.5" />
          {t('customerFulfillment.status.needsPurchase')}
        </span>
      );
    case 'AT_RISK':
      return (
        <span className={`${base} bg-red-50 text-red-700 border-red-200`}>
          <XCircle className="w-3.5 h-3.5" />
          {t('customerFulfillment.status.atRisk')}
        </span>
      );
    case 'NOT_DETERMINABLE':
    default:
      return (
        <span className={`${base} bg-gray-50 text-gray-700 border-gray-200`}>
          <HelpCircle className="w-3.5 h-3.5" />
          {t('customerFulfillment.status.notDeterminable')}
        </span>
      );
  }
}

function ReasonBadge({ reason }: { reason: FulfillmentReason }) {
  const { t } = useTranslation();
  if (reason === 'OK') return null;
  const colorMap: Record<FulfillmentReason, string> = {
    OK: 'bg-green-50 text-green-700 border-green-200',
    INSUFFICIENT_SUPPLY: 'bg-red-50 text-red-700 border-red-200',
    ETA_AFTER_DEADLINE: 'bg-red-50 text-red-700 border-red-200',
    NO_LINKED_PURCHASE: 'bg-red-50 text-red-700 border-red-200',
    NEEDS_PURCHASE_ON_TIME: 'bg-amber-50 text-amber-800 border-amber-200',
    NOT_DETERMINABLE: 'bg-gray-50 text-gray-700 border-gray-200',
    NO_PRODUCTION_ORDER: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border whitespace-nowrap ${colorMap[reason] || ''}`}
    >
      <AlertTriangle className="w-3 h-3" />
      {t(`customerFulfillment.reason.${reason}`)}
    </span>
  );
}

function ComponentRow({ comp }: { comp: ComponentEvaluation }) {
  const { t } = useTranslation();
  const linkedPos = comp.purchase_orders.filter((p) => p.linked_to_sales_order || comp.on_demand);
  const compStatus = deriveComponentStatus(comp);

  return (
    <div className="rounded-md border border-gray-200 bg-white p-2.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono text-xs font-semibold text-gray-900 shrink-0">
              {comp.component_product_code}
            </span>
            <span className="text-xs text-gray-700 truncate">{comp.component_product_name}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-blue-50 text-blue-700 border-blue-200">
              {comp.supply_type_label ||
                (comp.on_demand
                  ? t('customerFulfillment.component.onDemand')
                  : t('customerFulfillment.component.notOnDemand'))}
            </span>
            {compStatus !== 'OK' && <ReasonBadge reason={comp.reason} />}
          </div>
        </div>
        <StatusPill status={compStatus} />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-gray-700">
        <div className="flex items-center gap-1">
          <span className="text-gray-500">{t('customerFulfillment.component.demand')}:</span>
          <span className="font-medium text-gray-900">
            {formatNumber(comp.demand)} {comp.unit_abbreviation}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">{t('customerFulfillment.component.deadline')}:</span>
          <span className="font-medium text-gray-900">{formatDate(comp.deadline)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">{t('customerFulfillment.component.stock')}:</span>
          <span className="font-medium text-gray-900">{formatNumber(comp.stock_total)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">{t('customerFulfillment.component.transit')}:</span>
          <span className="font-medium text-gray-900">{formatNumber(comp.transit_attributable)}</span>
        </div>
        <div className="flex items-center gap-1 col-span-2">
          <span className="text-gray-500">{t('customerFulfillment.component.eta')}:</span>
          <span className="font-medium text-gray-900">{formatDate(comp.earliest_eta)}</span>
        </div>
      </div>

      {linkedPos.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <div className="flex items-center gap-1 text-[11px] font-medium text-gray-600 mb-1">
            <Truck className="w-3 h-3" />
            {t('customerFulfillment.component.linkedPurchases')}
          </div>
          <div className="space-y-1">
            {linkedPos.slice(0, 5).map((po) => (
              <div
                key={po.purchase_order_item_id}
                className="flex items-center justify-between gap-2 text-[11px] text-gray-700 bg-gray-50 rounded px-2 py-1"
              >
                <div className="min-w-0 truncate">
                  <span className="font-mono text-gray-900">{po.purchase_order_number || '—'}</span>
                  {po.supplier_name && (
                    <span className="text-gray-500"> · {po.supplier_name}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-gray-600">
                    {formatNumber(po.qty_attributed)} / {formatNumber(po.pending_qty)}
                  </span>
                  <span className="text-gray-500">{formatDate(po.eta)}</span>
                </div>
              </div>
            ))}
            {linkedPos.length > 5 && (
              <div className="text-[10px] text-gray-500 text-right">
                +{linkedPos.length - 5} {t('customerFulfillment.component.more')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LineCard({ line }: { line: LineEvaluation }) {
  const { t } = useTranslation();
  const lineStatus = deriveLineStatus(line);
  const needsAttention = lineStatus === 'AT_RISK' || lineStatus === 'NEEDS_PURCHASE_ON_TIME';
  const [expanded, setExpanded] = useState(needsAttention);

  const hasComponents = line.components && line.components.length > 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full text-left p-3 flex items-start justify-between gap-3 hover:bg-gray-50 rounded-lg"
      >
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono text-sm font-semibold text-gray-900 shrink-0">
              {line.product_code}
            </span>
            <span className="text-sm text-gray-700 truncate">{line.product_name}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
            <span>
              {t('customerFulfillment.line.qty')}:{' '}
              <span className="font-medium text-gray-900">
                {formatNumber(line.ordered_qty)} {line.unit_abbreviation}
              </span>
            </span>
            <span className="text-gray-300">·</span>
            <span>
              {t('customerFulfillment.line.deadline')}:{' '}
              <span className="font-medium text-gray-900">{formatDate(line.deadline)}</span>
            </span>
            <span className="text-gray-300">·</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-blue-50 text-blue-700 border-blue-200">
              {t(`customerFulfillment.case.${line.case}`)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {needsAttention && <ReasonBadge reason={line.reason} />}
          <StatusPill status={lineStatus} />
          {hasComponents && (expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ))}
        </div>
      </button>

      {expanded && hasComponents && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
          <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-2">
            {t('customerFulfillment.line.components')}
          </div>
          {line.components.map((c) => (
            <ComponentRow key={c.component_product_id} comp={c} />
          ))}
        </div>
      )}
    </div>
  );
}

export function CustomerFulfillmentDetailSidebar({
  salesOrderId,
  warehouseId,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'at_risk' | 'needs_purchase' | 'fulfilled'
  >('all');
  const [selectedFamilyKeys, setSelectedFamilyKeys] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen || !salesOrderId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setError(null);
    setSearch('');
    setStatusFilter('all');
    setSelectedFamilyKeys([]);
    customerFulfillmentService
      .getOrderDetail(salesOrderId, warehouseId ?? undefined)
      .then((res) => {
        if (cancelled) return;
        setDetail(res.order);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[CustomerFulfillment] detail error:', err);
        setError(err?.message || t('common.noData'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, salesOrderId, warehouseId, t]);

  const familyOptions = useMemo(() => {
    if (!detail) return [];
    const byKey = new Map<string, { key: string; label: string }>();
    for (const line of detail.lines) {
      const key = line.product_family_id !== null
        ? String(line.product_family_id)
        : '__no_family__';
      const label = line.product_family_name?.trim() || 'Sin familia';
      if (!byKey.has(key)) byKey.set(key, { key, label });
    }
    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [detail]);

  const filteredLines = useMemo(() => {
    if (!detail) return [];
    let lines = detail.lines;
    if (statusFilter === 'at_risk') {
      lines = lines.filter((l) => deriveLineStatus(l) === 'AT_RISK');
    } else if (statusFilter === 'needs_purchase') {
      lines = lines.filter((l) => deriveLineStatus(l) === 'NEEDS_PURCHASE_ON_TIME');
    } else if (statusFilter === 'fulfilled') {
      lines = lines.filter((l) => deriveLineStatus(l) === 'OK');
    }
    if (selectedFamilyKeys.length > 0) {
      const selected = new Set(selectedFamilyKeys);
      lines = lines.filter((l) => {
        const key = l.product_family_id !== null ? String(l.product_family_id) : '__no_family__';
        return selected.has(key);
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      lines = lines.filter(
        (l) =>
          (l.product_code || '').toLowerCase().includes(q) ||
          (l.product_name || '').toLowerCase().includes(q)
      );
    }
    return lines;
  }, [detail, statusFilter, selectedFamilyKeys, search]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[90] bg-black/20" onClick={onClose} aria-hidden />
      <aside className="fixed right-0 top-0 z-[91] h-full w-full max-w-2xl bg-white shadow-2xl border-l border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {detail
                ? `${t('customerFulfillment.sidebar.title')} · ${detail.order_number}`
                : t('customerFulfillment.sidebar.title')}
            </div>
            {detail && (
              <div className="mt-0.5 text-xs text-gray-600 truncate">
                {detail.customer}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-100 text-gray-600"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {detail && (
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3 text-xs text-gray-700">
            <div>
              <span className="text-gray-500">
                {t('customerFulfillment.sidebar.expectedDelivery')}:{' '}
              </span>
              <span className="font-medium text-gray-900">
                {formatDate(detail.expected_delivery_date)}
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('customerFulfillment.sidebar.lines')}: </span>
              <span className="font-medium text-gray-900">
                {detail.n_lines_evaluated}/{detail.n_lines_total}
              </span>
              {detail.n_lines_at_risk > 0 && (
                <span className="ml-1 text-red-700">
                  · ✗ {detail.n_lines_at_risk}
                </span>
              )}
              {detail.n_lines_needs_purchase !== undefined && detail.n_lines_needs_purchase > 0 && (
                <span className="ml-1 text-amber-700">
                  · ⏳ {detail.n_lines_needs_purchase}
                </span>
              )}
            </div>
            <StatusPill
              status={
                detail.fulfillment_status ||
                (detail.at_risk
                  ? 'AT_RISK'
                  : detail.needs_purchase
                  ? 'NEEDS_PURCHASE_ON_TIME'
                  : detail.complies
                  ? 'OK'
                  : 'NOT_DETERMINABLE')
              }
            />
          </div>
        )}

        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('customerFulfillment.sidebar.searchProduct')}
            className="flex-1 min-w-[160px] px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba]"
          />
          <div className="flex items-center gap-1 flex-wrap">
            {(['all', 'at_risk', 'needs_purchase', 'fulfilled'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setStatusFilter(opt)}
                className={`px-2 py-1 text-[11px] rounded border whitespace-nowrap ${
                  statusFilter === opt
                    ? 'bg-[#1681ba] text-white border-[#1681ba]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t(`customerFulfillment.sidebar.filter.${opt}`)}
              </button>
            ))}
          </div>
          {familyOptions.length > 0 && (
            <div className="w-full flex flex-wrap items-center gap-1 pt-1">
              <span className="text-[11px] text-gray-500 mr-1">Familias:</span>
              {familyOptions.map((family) => {
                const isActive = selectedFamilyKeys.includes(family.key);
                return (
                  <button
                    key={family.key}
                    type="button"
                    onClick={() =>
                      setSelectedFamilyKeys((prev) =>
                        prev.includes(family.key)
                          ? prev.filter((k) => k !== family.key)
                          : [...prev, family.key]
                      )
                    }
                    className={`px-2 py-1 text-[11px] rounded border whitespace-nowrap ${
                      isActive
                        ? 'bg-[#1681ba] text-white border-[#1681ba]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {family.label}
                  </button>
                );
              })}
              {selectedFamilyKeys.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFamilyKeys([])}
                  className="px-2 py-1 text-[11px] rounded border whitespace-nowrap bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                >
                  Limpiar
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]" />
            </div>
          )}
          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {error}
            </div>
          )}
          {!loading && !error && detail && filteredLines.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-8">
              {t('common.noData')}
            </div>
          )}
          {!loading && filteredLines.map((l) => (
            <LineCard key={l.sales_order_line_id} line={l} />
          ))}
        </div>
      </aside>
    </>,
    document.body
  );
}
